/**
 * Wire protocol between clients and server, plus strict validation.
 *
 * Design rule: `endAt` (a local time anchor) is NEVER transmitted, because the
 * clocks of iPad, phone and server are not synchronised. Instead the sender
 * transmits `remainingMs` computed at send time and the receiver re-anchors
 * `endAt = ownNow + remainingMs`. The error is roughly half the network latency,
 * which is irrelevant for an MM:SS display and never accumulates.
 */
import {
  isPresetId,
  remainingAt,
  type CoreStatus,
  type PresetId,
  type TimerState,
} from './state.js';

export const PROTOCOL_VERSION = 1;
export const MAX_MESSAGE_BYTES = 4096;

export type Role = 'display' | 'control';

export interface WireState {
  presetId: PresetId;
  durationMs: number;
  status: CoreStatus;
  /** Remaining time at the moment the message was created. */
  remainingMs: number;
  version: number;
  updatedAt: number;
  originId: string;
}

export function toWire(state: TimerState, now: number): WireState {
  return {
    presetId: state.presetId,
    durationMs: state.durationMs,
    status: state.status,
    remainingMs: remainingAt(state, now),
    version: state.version,
    updatedAt: state.updatedAt,
    originId: state.originId,
  };
}

export function fromWire(wire: WireState, now: number): TimerState {
  return {
    presetId: wire.presetId,
    durationMs: wire.durationMs,
    status: wire.status,
    remainingMs: wire.remainingMs,
    endAt: wire.status === 'running' ? now + wire.remainingMs : null,
    version: wire.version,
    updatedAt: wire.updatedAt,
    originId: wire.originId,
  };
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export interface Presence {
  displays: number;
  controls: number;
}

export type ClientMessage =
  | {
      type: 'hello';
      protocol: number;
      room: string;
      pin?: string;
      role: Role;
      clientId: string;
      state?: WireState;
    }
  | { type: 'state'; state: WireState }
  | { type: 'pong' };

export type ErrorCode = 'bad_room' | 'bad_pin' | 'invalid' | 'forbidden' | 'rate_limited';

export type ServerMessage =
  | { type: 'welcome'; canControl: boolean; presence: Presence }
  | { type: 'state'; state: WireState }
  | { type: 'presence'; presence: Presence }
  | { type: 'ping' }
  | { type: 'error'; code: ErrorCode; message: string };

// ---------------------------------------------------------------------------
// Validation (hand written, no dependencies)
// ---------------------------------------------------------------------------

const MAX_STRING = 256;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isNonNegInt(v: unknown): v is number {
  return isFiniteNumber(v) && Number.isInteger(v) && v >= 0;
}

function isShortString(v: unknown, max = MAX_STRING): v is string {
  return typeof v === 'string' && v.length > 0 && v.length <= max;
}

function isCoreStatus(v: unknown): v is CoreStatus {
  return v === 'idle' || v === 'running' || v === 'paused';
}

export function parseWireState(v: unknown): WireState | null {
  if (!isObject(v)) return null;
  const { presetId, durationMs, status, remainingMs, version, updatedAt, originId } = v;
  if (!isPresetId(presetId)) return null;
  if (!isNonNegInt(durationMs) || durationMs > 24 * 60 * 60 * 1000) return null;
  if (!isCoreStatus(status)) return null;
  if (!isFiniteNumber(remainingMs) || remainingMs < 0 || remainingMs > durationMs) return null;
  if (!isNonNegInt(version) || version > Number.MAX_SAFE_INTEGER) return null;
  if (!isNonNegInt(updatedAt)) return null;
  if (!isShortString(originId, 64)) return null;
  return {
    presetId,
    durationMs,
    status,
    remainingMs: Math.round(remainingMs),
    version,
    updatedAt,
    originId,
  };
}

export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case 'hello': {
      const { protocol, room, pin, role, clientId, state } = raw;
      if (protocol !== PROTOCOL_VERSION) return null;
      if (!isShortString(room)) return null;
      if (pin !== undefined && !isShortString(pin, 64)) return null;
      if (role !== 'display' && role !== 'control') return null;
      if (!isShortString(clientId, 64)) return null;
      let parsedState: WireState | undefined;
      if (state !== undefined) {
        const s = parseWireState(state);
        if (s === null) return null;
        parsedState = s;
      }
      const msg: ClientMessage = { type: 'hello', protocol, room, role, clientId };
      if (pin !== undefined) msg.pin = pin;
      if (parsedState) msg.state = parsedState;
      return msg;
    }
    case 'state': {
      const s = parseWireState(raw.state);
      return s ? { type: 'state', state: s } : null;
    }
    case 'pong':
      return { type: 'pong' };
    default:
      return null;
  }
}

function isPresence(v: unknown): v is Presence {
  return isObject(v) && isNonNegInt(v.displays) && isNonNegInt(v.controls);
}

const ERROR_CODES: readonly string[] = [
  'bad_room',
  'bad_pin',
  'invalid',
  'forbidden',
  'rate_limited',
];

export function parseServerMessage(raw: unknown): ServerMessage | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case 'welcome':
      if (typeof raw.canControl !== 'boolean' || !isPresence(raw.presence)) return null;
      return { type: 'welcome', canControl: raw.canControl, presence: raw.presence };
    case 'state': {
      const s = parseWireState(raw.state);
      return s ? { type: 'state', state: s } : null;
    }
    case 'presence':
      return isPresence(raw.presence) ? { type: 'presence', presence: raw.presence } : null;
    case 'ping':
      return { type: 'ping' };
    case 'error':
      if (!isShortString(raw.code) || !ERROR_CODES.includes(raw.code)) return null;
      return {
        type: 'error',
        code: raw.code as ErrorCode,
        message: typeof raw.message === 'string' ? raw.message.slice(0, MAX_STRING) : '',
      };
    default:
      return null;
  }
}

/** Parse a raw JSON text safely (size limited). Returns null on any problem. */
export function safeJsonParse(text: string): unknown {
  if (text.length > MAX_MESSAGE_BYTES) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
